import { type FileViewOptions } from "legacyComponents/FileUploadContainer.types";
import { BTFileSystem, type IHasAttachedFiles } from "legacyComponents/FileUploadContainer.types";
import {
    getBuilderOptions,
    getOwnerOptions,
    getSubsOptions,
} from "legacyComponents/FileUploadContainer.utils";

import {
    type FilePickerAttachmentsOptions,
    type FilePickerAttachmentsResult,
    type PickedAttachmentFile,
    type PickedTempFile,
    type PickedVideo,
} from "@btnet/utilities-webview";

import { FolderTypes, MediaType } from "types/enum";

import { FileUploadHandler } from "commonComponents/entity/media/BTFileUpload/BTFileUpload.api.handler";
import {
    AttachVideoRequest,
    EntityDocsRequest,
} from "commonComponents/entity/media/BTFileUpload/BTFileUpload.api.types";
import { getAttachmentOptions } from "commonComponents/entity/media/MediaAttachmentOptions/MediaAttachmentOptions.utils";

import { getTempFileTypeFromDocumentInstanceType } from "entity/media/common/media.utils";

function toToggleOption(option: FileViewOptions | undefined) {
    return {
        visible: option?.visible ?? false,
        canEdit: option?.canEdit ?? false,
        defaultValue: option?.defaultValue ?? false,
    };
}

function deriveMediaTypeFromMimeType(mimeType: string): MediaType {
    if (mimeType.startsWith("image/")) return MediaType.Photo;
    if (mimeType.startsWith("video/")) return MediaType.Video;
    return MediaType.Document;
}

function getExtensionFromFileName(fileName: string): string {
    const lastDot = fileName.lastIndexOf(".");
    return lastDot >= 0 ? fileName.slice(lastDot + 1) : "";
}

/**
 * Builds the options object to pass to the webview file picker bridge.
 * Derives all config from entity.attachedFiles.newFileOptions, which
 * the webview already has from the entity's initial load.
 *
 * @param correlationId - Device-local key for background uploads on a NEW entity
 *   (id === 0). Pass the id minted once for the create-form session so every
 *   picker open queues under the same key and the later resolve/abandon can
 *   address them (see resolveAttachmentCorrelation). Omit for existing entities,
 *   which attach immediately against their known entityId.
 * @param allowWebviewBackgroundUploads - Whether this entity opts into RN
 *   backgrounding uploads. A per-entity capability flag independent of
 *   `correlationId`: it applies to existing entities too (which queue under their
 *   known entityId), so it is set even when no correlationId is present. Maps to
 *   the contract's `allowBackgroundUploads`; named for webview here because it
 *   only affects the native picker path and is a no-op on web.
 */
export function buildWebviewPickerOptions(
    entity: IHasAttachedFiles,
    correlationId?: string,
    allowWebviewBackgroundUploads?: boolean,
): FilePickerAttachmentsOptions {
    const { newFileOptions, viewingPermissions } = entity.attachedFiles;
    const { showOptions, notifyOptions } = getAttachmentOptions(newFileOptions);

    return {
        tempFileType: getTempFileTypeFromDocumentInstanceType(entity.documentInstanceType),
        entityId: entity.id,
        documentInstanceType: entity.documentInstanceType,
        jobId: entity.jobId ?? undefined,
        allowBackgroundUploads: allowWebviewBackgroundUploads,
        ...(entity.id === 0 && correlationId !== undefined && { correlationId }),
        show: {
            builder: toToggleOption(getBuilderOptions(showOptions)),
            subs: toToggleOption(getSubsOptions(showOptions)),
            owner: toToggleOption(getOwnerOptions(showOptions)),
        },
        notify: {
            internalUsers: toToggleOption(getBuilderOptions(notifyOptions)),
            subs: toToggleOption(getSubsOptions(notifyOptions)),
            owner: toToggleOption(getOwnerOptions(notifyOptions)),
        },
        maxFileSize: viewingPermissions.maxFileSize ?? 0,
    };
}

/**
 * Builds a single BTFileSystem from a webview-picked file. Videos carry a Vimeo
 * `videoId` and have a known media type; everything else is a temp file keyed
 * on `tempFileId` with its media type derived from the mime type.
 */
function buildBTFileSystemFromPickedFile(
    file: PickedAttachmentFile,
    result: FilePickerAttachmentsResult,
): BTFileSystem {
    const common = {
        isTempFile: true,
        title: file.fileName,
        extension: getExtensionFromFileName(file.fileName),
        fileSize: file.fileSize,
        thumbnail: file.thumbnail ?? null,
        // Match the web upload path (BTFileUpload) so just-picked files can be removed
        // before save; other permissions stay false (temp files gate out draw/edit/rename).
        permissions: { canDelete: true },
        viewingPermissions: {
            showOwner: result.showOwner,
            showSubs: result.showSubs,
            showAllSubs: false,
            mainFile: false,
        },
        dateAttached: new Date(),
    };

    if (file.kind === "video") {
        return new BTFileSystem({
            ...common,
            // New-entity saves serialize through attachDocs, which keys a video off its
            // (temp) id, so the videoId must also be the id (tempId derives from it).
            id: file.videoId,
            videoId: file.videoId,
            mediaType: MediaType.Video,
            folderType: FolderTypes.NotAFolder,
        });
    }

    return new BTFileSystem({
        ...common,
        id: file.tempFileId,
        mediaType: deriveMediaTypeFromMimeType(file.mimeType),
        // The entity-attach endpoint trusts this flag from the request rather than
        // re-deriving it from the temp file, so it must be carried through here.
        is360Media: file.is360Media ?? false,
    });
}

/**
 * Converts the webview picker result into BTFileSystem objects.
 * Used for new entities (id === 0) where files are held in form state
 * until the entity is saved.
 */
export function buildBTFileSystemsFromWebviewResult(
    result: FilePickerAttachmentsResult,
): BTFileSystem[] {
    return result.files.map((file) => buildBTFileSystemFromPickedFile(file, result));
}

const defaultFileUploadHandler = new FileUploadHandler();

/** Builds the PUT /Videos/Attach request for a single webview-picked video. */
function buildAttachVideoRequest(
    entity: IHasAttachedFiles,
    video: PickedVideo,
    result: FilePickerAttachmentsResult,
): AttachVideoRequest {
    return new AttachVideoRequest({
        entityId: [entity.id],
        linkedType: entity.documentInstanceType,
        videoId: video.videoId,
        videoName: video.fileName,
        jobId: entity.jobId ?? undefined,
        showSubs: result.showSubs,
        showOwner: result.showOwner,
        notifyBuilder: result.notifyInternalUsers,
        notifySubs: result.notifySubs,
        notifyOwner: result.notifyOwner,
        // Description is set later by editing the video entity; attach sends empty.
        videoDescription: "",
        thumbnailUrl: video.thumbnail ?? "",
        fileSize: video.fileSize,
    });
}

/** Attaches webview-picked temp files (photos/documents) via /api/Documents/EntityDocs. */
async function attachWebviewTempFilesToExistingEntity(
    entity: IHasAttachedFiles,
    tempFiles: PickedTempFile[],
    result: FilePickerAttachmentsResult,
    handler: FileUploadHandler,
    nextViewingOrder?: number,
): Promise<BTFileSystem[]> {
    if (tempFiles.length === 0) {
        return [];
    }
    const fileSystems = tempFiles.map((file) => buildBTFileSystemFromPickedFile(file, result));
    const requestData = new EntityDocsRequest(
        entity.builderId,
        [entity.id],
        entity.jobId ?? null,
        entity.documentInstanceType,
        result.notifyInternalUsers,
        result.notifyOwner,
        result.notifySubs,
        fileSystems,
        nextViewingOrder,
    );
    const response = await handler.saveEntityDocs(requestData);
    return response.listOfAttachedFiles;
}

/**
 * Attaches webview-picked videos via PUT /api/Videos/Attach, one request per video.
 * Mirrors BTFileUpload's video attach: failures are swallowed per-video and only
 * the successfully attached videos are returned.
 */
async function attachWebviewVideosToExistingEntity(
    entity: IHasAttachedFiles,
    videos: PickedVideo[],
    result: FilePickerAttachmentsResult,
    handler: FileUploadHandler,
): Promise<BTFileSystem[]> {
    if (videos.length === 0) {
        return [];
    }
    const attachResults = await Promise.allSettled(
        videos.map((video) => handler.attachVideo(buildAttachVideoRequest(entity, video, result))),
    );
    const isFulfilled = <T>(p: PromiseSettledResult<T>): p is PromiseFulfilledResult<T> =>
        p.status === "fulfilled";
    return attachResults.filter(isFulfilled).map((r) => r.value);
}

/**
 * Attaches webview-picked files to an existing entity (id !== 0) where files must be
 * persisted immediately. Temp files (photos/documents) go through EntityDocs;
 * videos go through Videos/Attach. Returns the BTFileSystem objects from the server.
 */
export async function attachWebviewFilesToExistingEntity(
    entity: IHasAttachedFiles,
    result: FilePickerAttachmentsResult,
    handler = defaultFileUploadHandler,
    nextViewingOrder?: number,
): Promise<BTFileSystem[]> {
    const tempFiles = result.files.filter(
        (file): file is PickedTempFile => file.kind === "tempFile",
    );
    const videos = result.files.filter((file): file is PickedVideo => file.kind === "video");

    const [attachedTempFiles, attachedVideos] = await Promise.all([
        attachWebviewTempFilesToExistingEntity(
            entity,
            tempFiles,
            result,
            handler,
            nextViewingOrder,
        ),
        attachWebviewVideosToExistingEntity(entity, videos, result, handler),
    ]);

    return [...attachedTempFiles, ...attachedVideos];
}

/**
 * Shapes files selected from the Browse Buildertrend Files modal for attachment.
 * These are existing BT documents (not temp files), so we mark them as such and
 * preserve their existing viewing permissions rather than overriding them.
 */
export function shapeBrowsedBTFilesForAttachment(
    files: BTFileSystem[],
    isOnNewEntity: boolean,
): BTFileSystem[] {
    return files.map(
        (file) =>
            new BTFileSystem({
                ...file,
                existingBTDoc: true,
                isTempFile: false,
                isOnNewEntity,
                uniqueId: file.id,
            }),
    );
}

/**
 * Attaches existing BT files (selected via Browse) to an existing entity.
 * Sends no notifications and preserves each file's existing viewing permissions.
 * Returns the BTFileSystem objects from the server after attachment.
 */
export async function attachBrowsedBTFilesToExistingEntity(
    entity: IHasAttachedFiles,
    files: BTFileSystem[],
    handler = defaultFileUploadHandler,
    nextViewingOrder?: number,
): Promise<BTFileSystem[]> {
    const requestData = new EntityDocsRequest(
        entity.builderId,
        [entity.id],
        entity.jobId ?? null,
        entity.documentInstanceType,
        false,
        false,
        false,
        shapeBrowsedBTFilesForAttachment(files, false),
        nextViewingOrder,
    );
    const response = await handler.saveEntityDocs(requestData);
    return response.listOfAttachedFiles;
}
